import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import LangSwitcher from '../components/LangSwitcher';

const REMEMBER_KEY = 'hent_remember_email';

function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (searchParams.get('expired')) setError('Oturum süresi doldu, tekrar giriş yapın');
  }, [searchParams]);

  // Kayıtlı email'i yükle (Beni Hatırla)
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) { setEmail(saved); setRememberMe(true); }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result.success) {
        if (rememberMe) localStorage.setItem(REMEMBER_KEY, email.trim());
        else localStorage.removeItem(REMEMBER_KEY);
        navigate('/dashboard', { replace: true });
      } else setError(result.message || t('login.invalidCredentials'));
    } catch (err) {
      setError(err.response?.data?.message || t('common.error'));
    } finally { setLoading(false); }
  };

  return (
    <div className="hent-login">
      {/* Sol: marka + gradient panel */}
      <div className="hent-login-brand">
        <div className="hent-login-brand-inner">
          <img src="/logo.png" alt="Hent" className="hent-login-logo" />
          <h1>İşinizin Akıllı Merkezi</h1>
          <p>Stok, satış, satın alma, finans, CRM ve e-Belge — hepsi tek bulutta, mobilden ve web'den.</p>
          <ul className="hent-login-features">
            <li><span className="dot" />⚡ <strong>e-Fatura · e-Arşiv · e-İrsaliye</strong> entegre — GİB'e tek tık</li>
            <li><span className="dot" />🎯 <strong>CRM Pipeline</strong> · Web lead form · WhatsApp/Meta entegrasyon</li>
            <li><span className="dot" />💳 <strong>Çek-Senet · Plasiyer · Komisyon</strong> — Türk pazarına özel</li>
            <li><span className="dot" />📦 <strong>RFQ + Tedarikçi Portalı</strong> · Akıllı sipariş önerileri · 3-Way Match</li>
            <li><span className="dot" />🛒 <strong>E-Ticaret katalog · Online ödeme · Kargo entegre</strong></li>
            <li><span className="dot" />🎨 <strong>Özel Alanlar</strong> — kendi sektörel verilerinizi kod yazmadan ekleyin</li>
            <li><span className="dot" />📱 <strong>PWA + Native Push</strong> — telefondan saha, kapalı dağda offline çalışır</li>
            <li><span className="dot" />⌨ <strong>Ctrl+K Komut Paleti</strong> · Dark Mode · Kanban · Chatter</li>
            <li><span className="dot" />🤖 <strong>AI Veri Analizi</strong> · Pivot raporlar · Tedarikçi karnesi</li>
            <li><span className="dot" />🔐 <strong>Çok şirketli, RBAC</strong> · Onay hiyerarşisi · İşlem geçmişi</li>
          </ul>
        </div>
        <div className="hent-login-brand-foot">© 2026 Hent · İşinizin Akıllı Merkezi</div>
      </div>

      {/* Sağ: form */}
      <div className="hent-login-form">
        <div className="hent-login-form-inner">
          {/* Dil seçici — giriş ekranının sağ üstünde */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <LangSwitcher variant="compact" />
          </div>
          <img src="/logo.png" alt="Hent" className="hent-login-logo-mobile" />
          <h2>{t('login.signIn')}</h2>
          <p className="hent-login-sub">{t('login.subtitle')}</p>

          {error && (
            <div className="hent-login-error">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="hent-form-field">
              <label>{t('login.email')}</label>
              <div className="hent-input-wrap">
                <svg className="hent-input-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
                <input type="email" placeholder="ornek@firma.com" value={email}
                  onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email" />
              </div>
            </div>

            <div className="hent-form-field">
              <label>{t('login.password')}</label>
              <div className="hent-input-wrap">
                <svg className="hent-input-ico" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <input type={showPw ? 'text' : 'password'} placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                <button type="button" className="hent-input-toggle" onClick={() => setShowPw(!showPw)} tabIndex={-1} title={showPw ? 'Gizle' : 'Göster'}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {showPw
                      ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><line x1="1" y1="1" x2="23" y2="23" /></>
                      : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>}
                  </svg>
                </button>
              </div>
            </div>

            <div className="hent-login-row">
              <label className="hent-checkbox">
                <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                <span className="hent-checkbox-mark" />
                <span>{t('login.rememberMe')}</span>
              </label>
              <button type="button" className="hent-forgot-link" onClick={() => setShowForgot(true)}>
                {t('login.forgotPassword')}
              </button>
            </div>

            <button type="submit" className="hent-login-btn" disabled={loading}>
              {loading ? <><span className="hent-spinner" />{t('login.signingIn')}</> : t('login.signIn')}
            </button>
          </form>
        </div>
      </div>

      {/* Şifremi Unuttum modal'ı */}
      {showForgot && (
        <div className="hent-modal-overlay" onClick={() => setShowForgot(false)}>
          <div className="hent-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hent-modal-header">
              <h3>Şifrenizi mi Unuttunuz?</h3>
              <button className="hent-modal-close" onClick={() => setShowForgot(false)} title="Kapat">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="hent-modal-body">
              <p>Şifre sıfırlama işlemi için lütfen sistem yöneticinize başvurun. Yöneticiniz <b>Kullanıcılar</b> sayfasından şifrenizi yenileyebilir.</p>
              <div className="hent-modal-info">
                <div><b>Destek:</b> destek@asistanyazilim.com.tr</div>
                <div><b>Telefon:</b> 0 (XXX) XXX XX XX</div>
              </div>
            </div>
            <div className="hent-modal-foot">
              <button className="hent-login-btn" onClick={() => setShowForgot(false)}>Anladım</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
export default LoginPage;
